import { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { getStreamingSplit } from './streaming';

// Markdown renderer for streaming: no copy buttons (those appear once streaming completes).
const STREAMING_COMPONENTS = {
    table: ({ children, ...props }: any) => (
        <table className="table table-bordered" {...props}>{children}</table>
    ),
    thead: ({ children, ...props }: any) => (
        <thead className="table-light" {...props}>{children}</thead>
    ),
    th: ({ children, ...props }: any) => (
        <th scope="col" {...props}>{children}</th>
    ),
    code: ({ inline, className, children, ...props }: any) => {
        const match = /language-(\w+)/.exec(className || '');
        const codeString = String(children).replace(/\n$/, '');
        return !inline && match ? (
            <SyntaxHighlighter style={oneDark as any} language={match[1]} PreTag="div" {...props}>
                {codeString}
            </SyntaxHighlighter>
        ) : (
            <code className={className} {...props}>{children}</code>
        );
    }
};

// Memoised: only re-renders when the completed-paragraph text actually changes (i.e. at paragraph breaks).
const CompletedMarkdown = memo(({ text }: { text: string }) => (
    <ReactMarkdown
        className="message-content markdown-content"
        remarkPlugins={[remarkGfm]}
        skipHtml={true}
        components={STREAMING_COMPONENTS as any}
    >
        {text}
    </ReactMarkdown>
));

// Renders the in-flight streaming message: formatted completed paragraphs + plain-text current tail.
export const StreamingMessageContent = memo(({ text }: { text: string }) => {
    const { completed, tail } = getStreamingSplit(text);
    return (
        <div className="message-content markdown-content">
            {completed ? <CompletedMarkdown text={completed} /> : null}
            <span style={{ whiteSpace: 'pre-wrap' }}>{tail}</span>
        </div>
    );
});

type CompletedMessageProps = {
    text: string;
    copiedCode: string | null;
    onCopyCode: (codeString: string) => void;
};

// Renders a finished AI message: full markdown with per-code-block copy buttons.
export const CompletedMessage = ({ text, copiedCode, onCopyCode }: CompletedMessageProps) => (
    <ReactMarkdown
        className="message-content markdown-content"
        remarkPlugins={[remarkGfm]}
        skipHtml={true}
        components={{
            ...STREAMING_COMPONENTS,
            code: ({ inline, className, children, ...props }: any) => {
                const match = /language-(\w+)/.exec(className || '');
                const codeString = String(children).replace(/\n$/, '');

                return !inline && match ? (
                    <div className="code-container">
                        <SyntaxHighlighter style={oneDark as any} language={match[1]} PreTag="div" {...props}>
                            {codeString}
                        </SyntaxHighlighter>
                        <button
                            className={`code-copy-button ${copiedCode === codeString ? 'copied' : ''}`}
                            onClick={() => onCopyCode(codeString)}
                        >
                            <i className={`fas ${copiedCode === codeString ? 'fa-check' : 'fa-copy'}`}></i>
                        </button>
                    </div>
                ) : (
                    <code className={className} {...props}>{children}</code>
                );
            }
        } as any}
    >
        {text}
    </ReactMarkdown>
);
